import React, { useState, useRef } from 'react';
import { getUser } from '../services/auth';
import { meAPI } from '../services/api';
import Avatar from '../components/Avatar';
import PageHeader from '../components/PageHeader';
import './Profile.css';

/**
 * Simple profile page: shows the current user and lets them set/remove their avatar.
 */
function Profile() {
  const data = getUser();
  const user = data?.user;
  const userId = user?.id;
  const displayName = data?.staff_display_name || user?.username || user?.email || 'You';

  const [version, setVersion] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setBusy(true);
      setError(null);
      await meAPI.uploadAvatar(file);
      setVersion((v) => v + 1);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Upload failed');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleRemove = async () => {
    if (!window.confirm('Remove your photo?')) return;
    try {
      setBusy(true);
      setError(null);
      await meAPI.deleteAvatar();
      setVersion((v) => v + 1);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to remove photo');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="profile-page">
      <PageHeader title="Profile" />
      <div className="profile-card">
        <Avatar userId={userId} name={displayName} version={version} size={96} />
        <div className="profile-info">
          <div className="profile-name">{displayName}</div>
          {user?.email && <div className="profile-email">{user.email}</div>}
          {data?.tenant?.name && <div className="profile-tenant">{data.tenant.name}</div>}

          <div className="profile-actions">
            <label className="profile-upload-btn">
              {busy ? 'Uploading…' : 'Change photo'}
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={handleFile} disabled={busy} />
            </label>
            <button type="button" className="profile-remove-btn" onClick={handleRemove} disabled={busy}>
              Remove photo
            </button>
          </div>
          <p className="profile-hint">PNG, JPG, GIF, or WEBP · up to 5 MB. Others without a photo see your initials.</p>
          {error && <div className="profile-error" role="alert">{error}</div>}
        </div>
      </div>
    </div>
  );
}

export default Profile;
